import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ArticleCard } from '../components/article-card';
import { ChannelLogo } from '../components/channel-logo';
import { ChevronIcon } from '../components/icons';
import { useChannelSearchQuery } from '../hooks/use-channels-query';
import { useFeedSearch, useSavedQuery } from '../hooks/use-feed-query';

export function SearchResults() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const query = params.get('q') ?? '';

  const articles = useFeedSearch(query);
  const channels = useChannelSearchQuery(query);
  const saved = useSavedQuery();
  const savedSet = new Set(saved.data ?? []);

  const trimmed = query.trim();
  const articleList = articles.data ?? [];
  const channelList = channels.data ?? [];
  const empty =
    !articles.isLoading && !channels.isLoading && articleList.length === 0 && channelList.length === 0;

  return (
    <div className="nw-app nw-app--two">
      <aside className="nw-side">
        <div className="nw-side__label">{t('web.search.sidebar.label')}</div>
        <button type="button" className="nw-side__item" onClick={() => navigate('/home')}>
          <ChevronIcon dir="left" size={16} />
          <span className="nw-side__item-label">{t('web.search.sidebar.backToFeed')}</span>
        </button>
        <div className="nw-side__divider" />
        <div style={{ padding: '6px 14px', fontSize: 13, color: 'var(--text-secondary)' }}>
          {t('web.search.sidebar.resultsFor')}{' '}
          <strong style={{ color: 'var(--accent)' }}>{trimmed || '…'}</strong>
        </div>
      </aside>
      <section className="nw-feed">
        <div className="nw-feed__head">
          <div>
            <h1 className="nw-feed__title">{t('web.search.title')}</h1>
            <p className="nw-feed__sub">
              {trimmed
                ? t('web.search.subtitlePresent', { query: trimmed })
                : t('web.search.subtitleEmpty')}
            </p>
          </div>
        </div>

        {trimmed.length < 2 && (
          <div className="nw-feed__empty">{t('web.search.tooShort')}</div>
        )}

        {trimmed.length >= 2 && (articles.isLoading || channels.isLoading) && (
          <div className="nw-feed__loading">
            <div className="nw-feed__loading-spinner" aria-hidden />
            {t('web.search.loading')}
          </div>
        )}

        {trimmed.length >= 2 && empty && (
          <div className="nw-feed__empty">{t('web.search.empty', { query: trimmed })}</div>
        )}

        {channelList.length > 0 && (
          <div className="nw-explore-section">
            <h2 className="nw-explore-section__title">{t('web.search.channels')}</h2>
            <div className="nw-explore-grid">
              {channelList.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="nw-channel-row"
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/channel/${encodeURIComponent(c.id)}`)}
                >
                  <ChannelLogo name={c.name} logoUrl={c.logoUrl} size={40} />
                  <div className="nw-channel-row__body">
                    <div className="nw-channel-row__name">{c.name}</div>
                    <div className="nw-channel-row__meta">
                      {c.language?.toUpperCase()}
                      {c.country ? ` · ${c.country}` : ''}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {articleList.length > 0 && (
          <div className="nw-explore-section">
            <h2 className="nw-explore-section__title">{t('web.search.articles')}</h2>
            <div className="nw-feed__grid">
              {articleList.map((a) => (
                <ArticleCard key={a.id} article={a} isSaved={savedSet.has(a.id)} />
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
